import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authPut } from "../../../lib";

const useStyles = makeStyles((theme) => ({
  fields: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.5),
    width: 520,
    maxWidth: "100%",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  actions: { padding: theme.spacing(1, 3, 2) },
}));

/**
 * The three edit modals on a repair, as field lists on a 12-column grid.
 *
 * The server holds the same whitelists — this decides what to show, that
 * decides what may be written, and neither trusts the other.
 */
export const REPAIR_PANELS = {
  contact: {
    title: "Edit Contact Information",
    fields: [
      { name: "first_name", label: "First Name", col: 5 },
      { name: "mi", label: "MI", col: 2 },
      { name: "last_name", label: "Last Name", col: 5 },
      { name: "email", label: "Email", col: 7 },
      { name: "phone", label: "Phone", col: 5 },
    ],
  },
  shipping: {
    title: "Edit Shipping Information",
    fields: [
      { name: "ship_first_name", label: "First Name", col: 5 },
      { name: "ship_mi", label: "MI", col: 2 },
      { name: "ship_last_name", label: "Last Name", col: 5 },
      { name: "ship_address1", label: "Address 1", col: 12 },
      { name: "ship_address2", label: "Address 2", col: 12 },
      { name: "ship_city", label: "City", col: 6 },
      { name: "ship_state", label: "State", col: 3 },
      { name: "ship_zip", label: "Zip", col: 3 },
    ],
  },
  watch: {
    title: "Edit Watch Information",
    fields: [
      { name: "brand", label: "Brand", col: 6 },
      { name: "model", label: "Model", col: 6 },
      { name: "serial", label: "Serial No.", col: 6 },
      { name: "invoice", label: "Invoice #", col: 6 },
      { name: "purchase_date", label: "Purchase Date", col: 6, type: "date" },
    ],
  },
};

/**
 * Edits one panel of a record.
 *
 * Only changed fields are sent, so two people editing different halves of the
 * same panel do not overwrite each other.
 *
 * @param {string} endpoint  e.g. "/repairs/4250" — "/panel/<panel>" is appended
 */
export function ServicePanelDialog({ panel, endpoint, values, panels = REPAIR_PANELS, onClose, onSaved }) {
  const classes = useStyles();
  const spec = panels[panel];

  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!spec) return;
    const seed = {};
    spec.fields.forEach((f) => {
      const v = values?.[f.name];
      // A MySQL DATE arrives as an ISO timestamp; the date input wants yyyy-mm-dd.
      seed[f.name] = f.type === "date" ? String(v ?? "").slice(0, 10) : v ?? "";
    });
    setForm(seed);
    setError("");
  }, [panel, values, spec]);

  if (!spec) return null;

  const save = async () => {
    const body = {};
    spec.fields.forEach((f) => {
      const before = f.type === "date"
        ? String(values?.[f.name] ?? "").slice(0, 10)
        : values?.[f.name] ?? "";
      if (String(before) === String(form[f.name] ?? "")) return;
      body[f.name] = form[f.name];
    });

    if (!Object.keys(body).length) {
      onClose();
      return;
    }

    setBusy(true);
    const { error: err } = await authPut(`${endpoint}/panel/${panel}`, body);
    setBusy(false);

    if (err) {
      setError(err.message || "Could not save.");
      return;
    }
    onSaved();
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md">
      <DialogTitle>{spec.title}</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          {spec.fields.map((f) => (
            <TextField
              key={f.name}
              fullWidth
              label={f.label}
              type={f.type === "date" ? "date" : "text"}
              style={{ gridColumn: `span ${f.col ?? 12}` }}
              value={form[f.name] ?? ""}
              onChange={(e) =>
                setForm((s) => ({ ...s, [f.name]: e.target.value }))
              }
            />
          ))}
        </div>
        {error && <div className={classes.error}>{error}</div>}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} disabled={busy} size="small">
          Cancel
        </Button>
        <Button onClick={save} disabled={busy} size="small" variant="contained" color="primary">
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ServicePanelDialog;
